import { Button } from "@/components/ui/button.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { apiErrorMessage } from "@/shared/apiErrorMessage.ts";
import { isAxiosError } from "axios";
import { useState } from "react";
import { useLocation } from "wouter";
import { useSubmitPhoneNumber } from "./hooks/usePhoneVerification.tsx";

/**
 * "Verify your number to continue." For an account whose number was proven by
 * a claim link: one tap texts a code to that same number and moves on to the
 * code screen, which returns to `returnTo` once it is verified.
 */
export const ConfirmNumberStep = ({ returnTo }: { returnTo: string | null }) => {
  const [, setLocation] = useLocation();
  const { userContext } = useCurrentUserContext();
  const { mutateAsync: submitPhoneNumber, isPending } = useSubmitPhoneNumber();
  const [error, setError] = useState("");

  const phone = userContext?.user.userContact.handles?.find(
    (h) => h.type === "phoneNumber" && h.verified,
  )?.value;
  const back = returnTo ?? "/";

  const sendCode = async () => {
    if (!phone) return;
    setError("");
    try {
      // The number is the account's own, and its owner already agreed to texts
      // by writing in the group; the code is the only thing being asked for.
      const resp = await submitPhoneNumber({ phoneNumber: phone, smsConsent: true });
      if (resp.alreadyVerified) {
        setLocation(back, { replace: true });
        return;
      }
      setLocation(`/onboarding/verify-phone?returnTo=${encodeURIComponent(back)}`);
    } catch (err) {
      // Too many codes sent: the server names the wait, and "try again" would
      // only spend another send.
      const tooManySends = isAxiosError(err) && err.response?.data?.code === "TOO_MANY_SENDS";
      setError(
        (tooManySends ? apiErrorMessage(err) : undefined) ??
          "We couldn't send a code. Please try again.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-6 pt-16">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">Verify your number to continue</h1>
        <p className="text-muted-foreground">
          {phone
            ? `We'll text a code to ${phone}. Once it's verified you can connect accounts, change your number, and manage your account.`
            : "Add a phone number to continue."}
        </p>
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
      <div className="flex gap-2">
        <Button
          onClick={sendCode}
          disabled={!phone || isPending}
          data-statsig-name="step-up-send-code"
        >
          Text me a code
        </Button>
        <Button
          variant="outline"
          onClick={() => setLocation(back, { replace: true })}
          data-statsig-name="step-up-not-now"
        >
          Not now
        </Button>
      </div>
    </div>
  );
};
